import {useState} from 'react';
import {Box, Button, Grid, Group, NumberInput, TextInput, Text, Textarea, Divider} from '@mantine/core';
import {useForm} from '@mantine/form';
import {ArrowRight, CurrencyDollar} from "tabler-icons-react";
import {HomeUtils, IHome} from "features/homes"
import {
    IRefinance,
    IRefinanceFormValues
} from "features/homes/components/home.refinance-options/home.refinance-options.types";
import {DatePicker} from "@mantine/dates";

interface Props {
    onSubmitQuery?: (query: IRefinanceFormValues) => void,
    initialValues?: any
}

export function HomeRefinanceOptionsForm({onSubmitQuery, initialValues}: Props) {
    const [creatingHome, setCreatingHome] = useState(false);
    const form = useForm<IRefinanceFormValues>({
        initialValues: initialValues || {},
        validate: HomeUtils.getRefinanceValidationSchema(),
        validateInputOnBlur: true,
    });


    async function onSubmit(values: typeof form.values) {
        if (onSubmitQuery) {
            setCreatingHome(true);
            await onSubmitQuery(values)
            setCreatingHome(false);
        }
    }


    return (
        <Box mx="auto">
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Grid>
                    <Grid.Col span={12}>
                        <Text weight={600}>Current Loan Details</Text>
                        <Divider mt={3}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <NumberInput required icon={<Text>$</Text>} label="Remaining Loan Balance" hideControls
                                     parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                                     formatter={(value) =>
                                         !Number.isNaN(parseFloat(value!))
                                             ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                                             : ''
                                     } {...form.getInputProps("currentLoanBalance")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <NumberInput required label="Interest Rate (%)" hideControls {...form.getInputProps("currentInterestRate")}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <NumberInput parser={(value) => value?.replace(/\$\s?|(,*)/g, '')} formatter={(value) =>
                                         !Number.isNaN(parseFloat(value!))
                                             ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                                             : ''
                                     } required icon={<Text>$</Text>} label="Monthly Payment"
                                     placeholder="Principal, interest, taxes, insurance"
                                     hideControls {...form.getInputProps("monthlyPaymentAmount")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <NumberInput parser={(value) => value?.replace(/\$\s?|(,*)/g, '')} formatter={(value) =>
                                         !Number.isNaN(parseFloat(value!))
                                             ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                                             : ''
                                     } required icon={<Text>$</Text>} label="Original Loan Amount"
                                     hideControls {...form.getInputProps("originalLoanAmount")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <DatePicker required label="Original Loan Date" {...form.getInputProps("originalLoanDate")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <TextInput required label="Loan Type"
                                   placeholder="Conventional, FHA, VA, etc." {...form.getInputProps("currentLoanType")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <TextInput required label="Lender Name" {...form.getInputProps("currentLenderName")}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <NumberInput required label="Remaining Loan Term (in years)"
                                     hideControls {...form.getInputProps("loanRemainingYears")}/>
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Text weight={600}>Refinance Details</Text>
                        <Divider mt={3}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <Textarea required label="Reason for Refinance"
                                  placeholder="Lower rate, cash out, shorter term, etc." {...form.getInputProps("reasonForRefinance")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <NumberInput parser={(value) => value?.replace(/\$\s?|(,*)/g, '')} formatter={(value) =>
                                         !Number.isNaN(parseFloat(value!))
                                             ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                                             : ''
                                     } required icon={<Text>$</Text>} label="Desired Loan Amount"
                                     hideControls {...form.getInputProps("desiredLoanAmount")}/>
                    </Grid.Col>
                    <Grid.Col sm={6}>
                        <NumberInput required label="Preferred Loan Term (in years)" placeholder="15, 20, 30 years"
                                     hideControls {...form.getInputProps("preferredLoanTermYears")}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <NumberInput required label="Timeline for Closing (in days)"
                                     hideControls {...form.getInputProps("closingTimelineDays")}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <NumberInput parser={(value) => value?.replace(/\$\s?|(,*)/g, '')} formatter={(value) =>
                                         !Number.isNaN(parseFloat(value!))
                                             ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                                             : ''
                                     } icon={<Text>$</Text>} label="Cashout Amount Needed (if applicable)"
                                     hideControls {...form.getInputProps("cashOutAmount")}/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <Group position="right" mt="md">
                            <Button type="submit" loading={creatingHome}
                                    rightIcon={<ArrowRight/>}>
                                Submit
                            </Button>
                        </Group>
                    </Grid.Col>
                </Grid>
            </form>
        </Box>
    )
}
